import { useQuery } from '@apollo/client';
import { Box, Flex, VStack } from '@chakra-ui/react';

import asBentoPage from 'common/bento/hoc/asBentoPage';
import Button from 'components/_core/Button';
import Callout from 'components/_core/Callout';
import SunshineIcon from 'components/_core/SunshineIcon';
import Text from 'components/_core/Text';
import OBNavigation from 'features/Onboarding/components/OBNavigation/OBNavigation';
import { ApplicantType } from 'features/Onboarding/types';

import Header from '../../../../components/Header';
import MissingBeneficiaryEmailLink from '../../components/MissingBeneficiaryEmailLink';
import { PollVerificationModuleKybDocumentsDocument } from '../../graphql/queries/pollVerificationModuleKybDocuments.gql';
import getVerificationSteps from '../../lib/steps/getVerificationSteps';
import {
  VerificationStatus,
  VerificationStep,
  VerificationStepCategory,
} from '../../lib/steps/types';
import { State } from '../../machine';
import { VerificationModuleData } from '../../machine/model';
import triageLegalBeneficiaries from '../Home/libs/triageLegalBeneficiaries';
import Category from './components/Category';
import locales from './locales';

interface OnboardingHubProps {
  moduleData: VerificationModuleData;
  onStepClick: (step: VerificationStep) => void;
  onSubmitApplication: () => void;
  onBeneficiariesClick: () => void;
}

const OnboardingHub = asBentoPage<OnboardingHubProps>(
  ({
    Layout,
    moduleData,
    onBeneficiariesClick,
    onPrev,
    onStepClick,
    onSubmitApplication,
  }) => {
    const { company, companyUser } = moduleData.verificationData.viewer;
    const { lastKycReview } = companyUser;
    const {
      companyProfileId,
      lastKybReview,
      legalBeneficiaries,
      metadata: companyMetadata,
    } = company;

    const beneficiaries = triageLegalBeneficiaries({ legalBeneficiaries });
    const isSomeBeneficiaryEmailMissing = Boolean(
      beneficiaries?.isMissingSomeEmail,
    );

    const steps = getVerificationSteps(moduleData);

    const hasInvalidSteps =
      steps.some(({ status }) => status === VerificationStatus.Invalid) ||
      isSomeBeneficiaryEmailMissing;

    const numberOfRemainingSteps = steps.filter(
      ({ status }) =>
        status !== VerificationStatus.Completed &&
        status !== VerificationStatus.Validated,
    ).length;
    const areAllStepsCompleted =
      numberOfRemainingSteps === 0 && !isSomeBeneficiaryEmailMissing;

    const isFirstSubmission = !lastKycReview && !lastKybReview;

    // This polling is used to handle the auto-fetching of the statutes.
    // If we succeed to download them in the background, its status will instantly
    // be changed to COMPLETED to avoid confusing the user.
    useQuery(PollVerificationModuleKybDocumentsDocument, {
      pollInterval: 2000,
      skip: !steps.some(
        ({ id, status }) =>
          [State.STATUTES, State.COMPANY_PAPERS].includes(id) &&
          status === VerificationStatus.ToDo,
      ),
      variables: {
        companyProfileId,
      },
    });

    return (
      <Layout
        data-testid="onboarding-hub"
        nav={
          <OBNavigation
            onPrev={
              moduleData.applicantType === ApplicantType.Mandatee
                ? onPrev
                : undefined
            }
          />
        }
        variant="larger"
      >
        <Box paddingBottom="space-64">
          <Header
            description={
              hasInvalidSteps ? locales.errorSubtitle : locales.subtitle
            }
            title={locales.title}
          />

          {/* The KYC review error might be an error message linked to no step, so we always display it.
          To handle the case where it was linked to a step and there's no remaining invalid step,
          we display it differently so it doesn't prevent the user from submitting the application. */}
          {lastKycReview?.error ? (
            <Callout
              marginY="space-40"
              variant={hasInvalidSteps ? 'warning' : 'info'}
            >
              <Callout.Content>
                {hasInvalidSteps ? null : (
                  <Text fontWeight="weight-500">
                    {locales.originalErrorMessage}
                  </Text>
                )}

                {lastKycReview.error}
              </Callout.Content>
            </Callout>
          ) : null}

          <VStack gap="space-16">
            {[
              VerificationStepCategory.PersonalInfo,
              VerificationStepCategory.CompanyInfo,
              VerificationStepCategory.ShineOffer,
            ].map((category) => (
              <Category
                category={category}
                displayOnlyRemainingSteps={!isFirstSubmission}
                key={category}
                onStepClick={onStepClick}
                verificationSteps={steps}
              />
            ))}
          </VStack>

          {/* As we only show invalid steps when it's not the 1st submission,
            we'll display some text when they've all be completed and there's no step to display */}
          {!isFirstSubmission && areAllStepsCompleted ? (
            <Box>
              <Flex alignItems="center" gap="space-8">
                <SunshineIcon
                  color="success"
                  name="tick-in-circle"
                  size="icon-16"
                />

                <Text fontWeight="weight-500">
                  {locales.applicationCompleted.title}
                </Text>
              </Flex>

              <Text size="sm" variant="secondary">
                {locales.applicationCompleted.subtitle}
              </Text>
            </Box>
          ) : null}

          <Flex justify="flex-end">
            <Button
              alignSelf="right"
              icon="arrow-right"
              iconPosition="right"
              iconSpacing="space-4"
              isDisabled={!areAllStepsCompleted}
              marginTop="space-48"
              onClick={onSubmitApplication}
            >
              {isFirstSubmission
                ? locales.submitApplication.send
                : locales.submitApplication.resend}
            </Button>
          </Flex>

          {isSomeBeneficiaryEmailMissing &&
          companyMetadata.hasRegisteredAllBeneficiaries ? (
            <MissingBeneficiaryEmailLink onClick={onBeneficiariesClick} />
          ) : null}
        </Box>
      </Layout>
    );
  },
);

export default OnboardingHub;
